<template>
  <div class="nominations-list my-4">
    <strong class="event_name h5">{{ event.name }}</strong>
    <grid-table
      :columns="tableColumns"
      :rows="nominations"
      :hide-header="false"
      no-stripes
    >
      <template #cell:select="{ row }">
        <app-checkbox
          :id="'reassign-' + row.id"
          :value="value.indexOf(row.id) !== -1"
          @input="$emit('toggle', row.id, $event)"
        />
      </template>
      <template #cell:competitorName="{ row }">
        {{ row.competitorName }}
        <small v-if="row.competitorDeleted" class="text-primary ml-1">(deleted)</small>
      </template>
      <template #cell:horseName="{ row }">
        <input
          v-if="row.noHorse"
          type="number"
          min="1"
          :max="quantityMaxByNomId[row.id]"
          :disabled="quantityUpdatingIds.indexOf(row.id) !== -1"
          class="form-control nomination-event-table__quantity-input"
          :value="quantityOverrides[row.id] != null ? quantityOverrides[row.id] : row.quantity"
          @change="onQuantityChange(row.id, $event.target.value)"
        />
        <template v-else>{{ row.horseName }}</template>
        <small v-if="row.horseDeleted" class="text-primary ml-1">(deleted)</small>
        <small v-if="row.combined" class="text-primary ml-1">(Combined)</small>
      </template>
      <template #cell:paid="{ row }">
        <span :class="row.paid ? 'text-success' : 'text-danger'">
          {{ row.paid ? 'Paid' : 'Unpaid' }}
        </span>
      </template>
    </grid-table>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from '@vue/composition-api'
import GridTable from '@/components/GridTable'
import AppCheckbox from '@/components/AppCheckbox'
import EventBus from '@/store/EventBus'
import nominationsApi from '@/services/api/nominations'

export default defineComponent({
  name: 'NominationEventTable',
  components: { GridTable, AppCheckbox },
  props: {
    event: { type: Object, required: true },
    nominations: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] }
  },
  emits: ['toggle', 'reload'],
  setup(props, { emit }) {
    // All nominations in a group belong to the same event, so the same event either
    // requires a horse for all rows or none — checking the first row is enough.
    const isNoHorseEvent = computed(() => props.nominations.length > 0 && props.nominations[0].noHorse)

    const tableColumns = computed(() => [
      { key: 'select', label: '', width: '40px' },
      { key: 'competitorName', label: 'Competitor name', width: '1fr' },
      { key: 'horseName', label: isNoHorseEvent.value ? 'Quantity' : 'Horse name', width: '1fr' },
      { key: 'paid', label: 'Payment status', width: '140px' }
    ])

    // Remaining Quantity quota per nomination, keyed by nomination id — mirrors the backend's
    // per-person max_submissions check (this member's other quantities in this same event group,
    // capped at event.maxSubmissions). Same pattern as NominationsAddList.vue.
    const quantityMaxByNomId = computed(() => {
      const result = {}
      if (props.event.maxSubmissions == null) return result
      const totalsByMember = {}
      props.nominations.forEach((n) => {
        totalsByMember[n.userMemberId] = (totalsByMember[n.userMemberId] || 0) + (n.quantity || 0)
      })
      props.nominations.forEach((n) => {
        result[n.id] = props.event.maxSubmissions - (totalsByMember[n.userMemberId] - (n.quantity || 0))
      })
      return result
    })

    const quantityOverrides = ref({})
    const quantityUpdatingIds = ref([])

    async function handleQuantityChange(nomId, quantity) {
      quantityUpdatingIds.value = quantityUpdatingIds.value.concat(nomId)
      try {
        // keep_original_status: this is secretary-only tooling (Re-assign nominations), same
        // spirit as the Account Detail popup on the Nom Dashboard — a quantity increase's new
        // sibling row inherits this nomination's own status rather than the Nominations page's
        // Unprocessed/Waitlist-by-draft-state default (ACA didn't cover this page explicitly).
        const res = await nominationsApi.updateNomination(nomId, { quantity, keep_original_status: true })
        if (res.data.message) EventBus.$emit('show-success', res.data.message)
        emit('reload')
      } catch (err) {
        const reverted = { ...quantityOverrides.value }
        delete reverted[nomId]
        quantityOverrides.value = reverted
        const msg = err.response != null && err.response.data != null ? err.response.data.message : 'Failed to update quantity'
        EventBus.$emit('http-error', msg)
      } finally {
        quantityUpdatingIds.value = quantityUpdatingIds.value.filter(id => id !== nomId)
      }
    }

    function onQuantityChange(nomId, value) {
      let quantity = Number(value)
      if (!Number.isInteger(quantity) || quantity < 1) return
      const max = quantityMaxByNomId.value[nomId]
      if (max != null && quantity > max) quantity = max
      quantityOverrides.value = { ...quantityOverrides.value, [nomId]: quantity }
      handleQuantityChange(nomId, quantity)
    }

    return { tableColumns, quantityMaxByNomId, quantityOverrides, quantityUpdatingIds, onQuantityChange }
  }
})
</script>

<style scoped>
.nomination-event-table__quantity-input {
  max-width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/Nominations/NominationEventTable.vue